<template>
  <Modal
    :title="title"
    name="CREATE_EDIT_REAL_ESTATE_USER_MODAL"
    ref="createUpdateRealEstateUserModal"
    width="600"
  >
    <template v-slot:body>
      <v-progress-linear v-show="isLoading" indeterminate />
      <v-form v-show="!isLoading" v-model="valid" ref="realEstateUserForm" lazy-validation>

        <v-tabs v-if="isInspector" v-model="currentTab" color="realEstate">
          <v-tab>
            {{ labels.information }}
          </v-tab>
          <v-tab>
            {{ labels.activeServices }}
          </v-tab>
        </v-tabs>

        <v-card elevation="0" :class="isInspector ? 'px-5 pt-3 pb-5' : 'pa-0'" :outlined="isInspector">
          <v-tabs-items v-model="currentTab">
            <v-tab-item>
              <v-row>
                <v-col class="pb-0" cols="12" sm="9">
                  <v-text-field
                    v-model="name"
                    :label="$t('realEstateUsers.label.name')"
                    :rules="rules.required"
                    outlined
                  />
                </v-col>
                <v-col class="pb-0" v-if="realEstateUser" cols="12" sm="3">
                  <v-select
                    v-model="enabled"
                    :label="labels.isActive"
                    :items="userStatus"
                    item-value="value"
                    item-text="label"
                    outlined
                  />
                </v-col>
              </v-row>

              <v-text-field
                v-model="email"
                :label="$t('realEstateUsers.label.email')"
                :rules="rules.emailRule"
                autocomplete="new-user"
                outlined
              />

              <HPhone v-model="phone" :default-value="phone" :country="language" />

              <v-row v-if="!realEstateUser">
                <v-col class="py-0" cols="12" md="6">
                  <v-text-field
                    v-model="rg"
                    :label="$t('realEstateUsers.label.rg')"
                    type="number"
                    outlined
                  />
                </v-col>
                <v-col class="py-0" cols="12" md="6">
                  <HDocumentID
                    v-model="cpf"
                    :label="$t('realEstateUsers.label.cpf')"
                    :country="language"
                  />
                </v-col>
              </v-row>

              <v-select
                v-model="branch"
                :label="$t('realEstateUsers.label.branch')"
                :rules="rules.required"
                :items="branches"
                item-value="id"
                item-text="name"
                return-object
                outlined
              />

              <v-select
                v-model="role"
                :disabled="!hasInternalWorkerType"
                :label="$t('realEstateUsers.label.role')"
                :rules="rules.required"
                :items="roles"
                hide-details="auto"
                item-value="id"
                item-text="name"
                return-object
                outlined
              />

              <div v-if="!realEstateUser">
                <h4 class="mt-5">
                  {{ $t('hausefulUsers.label.password') }}
                </h4>

                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="password"
                      :type="type"
                      :append-icon="icon"
                      :rules="rules.passwordRule"
                      :label="$t('realEstateUsers.label.password')"
                      @click:append="handleShowPassword"
                      autocomplete="new-password"
                      hide-details="auto"
                      outlined
                    />
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="passwordMatch"
                      :type="type"
                      :rules="[rules.matchRule(password, passwordMatch)]"
                      :label="$t('realEstateUsers.label.repeatPassword')"
                      hide-details="auto"
                      outlined
                    />
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

            <v-tab-item>
              <ServiceTypesSelection
                v-if="!isLoading"
                @update:service-types="updateServiceTypes"
                :current-service-types="currentServiceTypesForSelection"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <div class="d-flex justify-end mt-6 gap-1">
          <v-btn
            @click="closeAllModals"
            :disabled="isLoading"
            class="grey--text mr-5"
            elevation="0"
            large
            text
          >
            {{ $t('hausefulUsers.action.cancel') }}
          </v-btn>

          <v-btn
            @click="validate"
            :loading="isLoading"
            color="primary"
            :disabled="!valid"
            elevation="0"
            large
          >
            {{ saveLabel }}
          </v-btn>
        </div>
      </v-form>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

// enums
import { countriesList } from '../../util/enums'

// utils
import {
  required,
  emailRule,
  matchRule,
  documentRule,
  passwordRule,
} from '@/util/defaultValidationRules'

// components
import Modal from '@/components/app/Modal'
import HPhone from '../app/HPhone'
import HDocumentID from '../app/HDocumentID'
import HCountrySelect from '../app/HCountrySelect'
import ServiceTypesSelection from '../../views/hausefulUsers/fragments/ServiceTypesSelection.vue'

export default {
  name: 'RealEstateUserCreateUpdate',

  components: {
    Modal,
    HPhone,
    HDocumentID,
    HCountrySelect,
    ServiceTypesSelection,
  },

  data() {
    return {
      valid: false,
      id: null,
      name: null,
      email: null,
      rg: null,
      cpf: null,
      phone: null,
      branch: null,
      role: null,
      password: null,
      passwordMatch: null,
      isTester: false,
      enabled: true,
      language:
        countriesList.find(
          (country) =>
            country.language_code === localStorage.getItem('user_language')
        )?.code ?? 'PT_BR',
      currency:
        countriesList.find(
          (country) =>
            country.currency_code === localStorage.getItem('user_currency')
        )?.currency ?? 'BRAZILIAN_REAL',
      type: 'password',
      icon: 'mdi-eye-outline',

      rules: {
        required,
        emailRule,
        matchRule,
        documentRule,
        passwordRule,
      },

      userStatus: [
        { value: true,  label: this.$t('hausefulUsers.label.yes') },
        { value: false,  label: this.$t('hausefulUsers.label.no') },
      ],

      labels: {
        services: this.$t('hausefulUsers.label.services'),
        information: this.$t('hausefulUsers.label.information'),
        location: this.$t('hausefulUsers.label.location'),
        activeServices: this.$t('hausefulUsers.label.activeServices'),
        noServiceTypesSelected: this.$t('hausefulUsers.messages.noServiceTypesSelected'),
        selectServiceTypes: this.$t('hausefulUsers.messages.selectServiceTypes'),
        isActive: this.$t('realEstateUsers.label.isActive'),
      },

      selectedServiceTypes: [],
      currentTab: 0,
    }
  },

  watch: {
    realEstateUser(currentUser) {
      Promise.resolve(currentUser).then(() => {
        const currentId = currentUser?.id

        if (currentId) this.fillUserFields()
        else this.resetUserFields()
      })
    },

    activeModal(modalName) {
      if (modalName === 'CREATE_EDIT_REAL_ESTATE_USER_MODAL') {
        if (!this.branches?.length) {
          this.fetchBranches()
        }
      }
    },
  },

  computed: {
    ...mapGetters([
      'isLoading',
      'realEstateUser',
      'branches',
      'activeModal',
      'currentRealEstate',
    ]),

    title() {
      return this.isLoading
        ? this.$t('realEstateUsers.label.loading')
        : this.realEstateUser
        ? this.$t('realEstateUsers.label.edit')
        : this.$t('realEstateUsers.label.create')
    },

    hasInternalWorkerType() {
      const workerTypes = this.currentRealEstate?.worker_types

      if (!Array.isArray(workerTypes) || workerTypes.length === 0) {
        return false
      }

      const hasInternal = workerTypes.includes('INTERNAL')

      if (hasInternal) {
        return true
      }

      return false
    },

    roles() {
      const baseRoles = [
        {
          id: 'ROLE_REAL_ESTATE',
          name: this.$t('realEstate.roles.operator')
        },
      ]

      const inspectorRole = {
        id: 'ROLE_INSPECTOR',
        name: this.$t('realEstate.roles.surveyor'),
      }

      if (this.hasInternalWorkerType) {
        return [...baseRoles, inspectorRole]
      }

      return baseRoles
    },

    isInspector() {
      return this.realEstateUser?.type === 'ROLE_INSPECTOR'
    },

    saveLabel() {
      return this.realEstateUser
        ? this.$t('realEstateUsers.label.edit')
        : this.$t('realEstateUsers.label.create')
    },

    currentServiceTypesForSelection() {
      return (this.realEstateUser && this.realEstateUser.service_types)
        ? this.realEstateUser.service_types
        : []
    },
  },

  methods: {
    ...mapMutations([
      'closeAllModals',
    ]),

    ...mapActions([
      'fetchBranches',
      'editRealEstateUser',
      'createRealEstateUsers',
      'fetchCurrentRealEstate'
    ]),

    async validate() {
      const form = this.$refs.realEstateUserForm
      const isValid = await form.validate()

      if (!isValid) return

      const realEstateUser = {
        id: this.id ? this.id : null,
        payload: {
          rg: this.rg && this.rg,
          cpf: this.cpf && this.cpf.replace(/[^\d]+/g, ''),
          name: this.name,
          username: this.email,
          password: this.password,
          phone: this.phone,
          cityId: this.branch
            ? this.branch.city.id
            : this.realEstateUser.branch.city.id,
          branchId: this.branch
            ? this.branch.id
            : this.realEstateUser.branch.id,
          type: this.role?.id ?? 'ROLE_REAL_ESTATE',
          tester: this.isTester,
          enabled: this.enabled,
          language: this.language,
          currency: this.currency,
          service_types: this.selectedServiceTypes,
        },
      }

      const realEstateUserData = { realEstateUser, form }

      this.id === null
        ? this.createRealEstateUsers(realEstateUserData)
        : this.editRealEstateUser(realEstateUserData)

      this.currentTab = 0
    },

    handleShowPassword() {
      if (this.type === 'password') {
        this.type = 'text'
        this.icon = 'mdi-eye-off-outline'
      } else {
        this.type = 'password'
        this.icon = 'mdi-eye-outline'
      }
    },

    resetUserFields() {
      this.id = null
      this.name = ''
      this.email = ''
      this.rg = ''
      this.cpf = ''
      this.phone = ''
      this.branch = null
      this.role = this.roles.find((r) => r.id === 'ROLE_REAL_ESTATE')
      this.enabled = true
      this.isTester = false
      this.selectedServiceTypes = []

      this.$refs.realEstateUserForm && this.$refs.realEstateUserForm.resetValidation()
    },

    fillUserFields() {
      this.id = this.realEstateUser.id
      this.name = this.realEstateUser.name
      this.email = this.realEstateUser.username
      this.rg = this.realEstateUser.rg
      this.cpf = this.realEstateUser.cpf
      this.phone = this.realEstateUser.phone
      this.branch = this.realEstateUser.branch
      this.role = this.roles.find(
        (role) =>
          role.id ===
          (this.hasInternalWorkerType
            ? this.realEstateUser.type
            : 'ROLE_REAL_ESTATE')
      )
      this.enabled = this.realEstateUser.enabled
      this.isTester = this.realEstateUser.is_tester
      this.selectedServiceTypes = this.realEstateUser.service_types ?? []
    },

    updateServiceTypes(serviceTypes) {
      this.selectedServiceTypes = serviceTypes
    },
  },

  mounted() {
    this.role = this.roles.find((role) => role.id === 'ROLE_REAL_ESTATE')
  },
}
</script>
